import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import addMinutes from 'date-fns/addMinutes';
import { TFunction } from 'i18next';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'src/components/image';
import { useTranslatedSnackbar } from 'src/hooks/useTranslatedSnackbar';
import MenuPopover from 'src/components/menu-popover';
import * as Yup from 'yup';
import Iconify from '../iconify/Iconify';

import { Stack } from '@mui/system';
import { AxiosResponse } from 'axios';
import { LIGHT_BACKGROUND } from 'src/theme/palette';
import {
  convertDateToTimeString,
  convertTimeStringToDate,
  convertTimeStringToMinutes,
} from 'src/utils/DateTimeConvert';
import { AvailabilitySlotDto } from 'woofwoof-api';

export type SlotProps = {
  start: string;
  end: string;
  weekday: number;
  organizationName?: string;
};

export type ExtendedAvailabilitySlotDto = AvailabilitySlotDto & {
  organizationName: string;
};

interface CalendarScheduleProps {
  isLoading?: boolean;
  data?: ExtendedAvailabilitySlotDto[];
  refetch: () => void;
  timeOff?: Array<object>;
  onSave: (
    availabilitySlots: AvailabilitySlotDto[],
  ) => Promise<AxiosResponse<any>>;
  isReadonly?: boolean;
  organizationWorkingHours?: AvailabilitySlotDto[];
}

function getAvailabilitySlotYupSchema(t: TFunction) {
  return Yup.object().shape({
    start: Yup.string()
      .required(String(t('validation.fieldRequired')))
      .matches(
        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
        String(t('validation.timeFormat')),
      )
      .notOneOf(['00:00'], String(t('validation.hourNotMidnight'))),
    end: Yup.string()
      .required(String(t('validation.fieldRequired')))
      .matches(
        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
        String(t('validation.timeFormat')),
      )
      .notOneOf(['00:00'], String(t('validation.hourNotMidnight'))),
    weekday: Yup.number()
      .required(String(t('validation.fieldRequired')))
      .min(0)
      .max(6),
  });
}

const CalendarSchedule: React.FC<CalendarScheduleProps> = ({
  isLoading,
  data,
  refetch,
  timeOff,
  onSave,
  isReadonly,
  organizationWorkingHours,
}) => {
  const { t, i18n } = useTranslation();
  const { enqueueTranslated } = useTranslatedSnackbar();
  const { enqueueSnackbar } = useSnackbar();
  const [isTimePickerChanged, setIsTimePickerChanged] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [popoverContent, setPopoverContent] = useState<string>('');
  const [slotErrors, setSlotErrors] = useState<{
    [weekday: number]: {
      [slotIndex: number]: { start: boolean; end: boolean };
    };
  }>({});

  // Sort data by weekday first, then by start time within each weekday
  const sortedData = data
    ? [...data].sort((a, b) => {
        if (a.weekday !== b.weekday) {
          return a.weekday - b.weekday;
        }
        return a.start.localeCompare(b.start);
      })
    : [];

  const dataAvailabilitySlots = sortedData;
  const [availabilitySlots, setAvailabilitySlots] = useState<SlotProps[]>(
    dataAvailabilitySlots || [],
  );

  // Helper function to check if two time slots overlap
  // This should only return true if the slots actually conflict (not if one is contained within another)
  const doTimeSlotsOverlap = (
    slot1: { start: string; end: string; weekday: number },
    slot2: { start: string; end: string; weekday: number },
  ): boolean => {
    if (slot1.weekday !== slot2.weekday) return false;

    const start1 = convertTimeStringToMinutes(slot1.start);
    const end1 = convertTimeStringToMinutes(slot1.end);
    const start2 = convertTimeStringToMinutes(slot2.start);
    const end2 = convertTimeStringToMinutes(slot2.end);

    // Allow if slot1 is completely within slot2 (professional can work during organization hours)
    if (start1 >= start2 && end1 <= end2) {
      return false;
    }

    // Allow if slot2 is completely within slot1
    if (start2 >= start1 && end2 <= end1) {
      return false;
    }

    // Conflict if they overlap (including touching at boundaries)
    // Use <= to catch boundary cases like 12:00-15:00 vs 15:00-17:00
    return start1 <= end2 && start2 <= end1;
  };

  // Helper function to check for conflicts with other organization slots
  const checkSlotConflicts = (newSlot: {
    start: string;
    end: string;
    weekday: number;
  }): string[] => {
    const conflicts: string[] = [];

    dataAvailabilitySlots.forEach((existingSlot) => {
      if (
        existingSlot.organizationName &&
        doTimeSlotsOverlap(newSlot, existingSlot)
      ) {
        if (!conflicts.includes(existingSlot.organizationName)) {
          conflicts.push(existingSlot.organizationName);
        }
      }
    });

    return conflicts;
  };

  const formattedSlots = availabilitySlots.map((slot) => ({
    ...slot,
    start: slot.start,
    end: slot.end,
  }));

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        formattedSlots: Yup.array().of(getAvailabilitySlotYupSchema(t)),
      }),
    [t],
  );

  const weekdaysLong = useMemo(
    () => [
      t('calendar.weekdayLongMon'),
      t('calendar.weekdayLongTue'),
      t('calendar.weekdayLongWed'),
      t('calendar.weekdayLongThu'),
      t('calendar.weekdayLongFri'),
      t('calendar.weekdayLongSat'),
      t('calendar.weekdayLongSun'),
    ],
    [t],
  );

  const weekdaysShort = useMemo(
    () => [
      t('calendar.weekdayShortMon'),
      t('calendar.weekdayShortTue'),
      t('calendar.weekdayShortWed'),
      t('calendar.weekdayShortThu'),
      t('calendar.weekdayShortFri'),
      t('calendar.weekdayShortSat'),
      t('calendar.weekdayShortSun'),
    ],
    [t],
  );

  const dateLocale = i18n.language.startsWith('pl') ? 'pl-PL' : 'en-US';

  const sortSlotsByDay = (slots: SlotProps[]) => {
    return slots.sort((a, b) => {
      if (a.weekday === 0) return 1;
      if (b.weekday === 0) return -1;
      return a.weekday - b.weekday;
    });
  };

  const sortedAvailabilitySlots = sortSlotsByDay(formattedSlots);

  const groupSlotsByDay = (slots: SlotProps[]) => {
    return slots.reduce((acc: { [key: number]: SlotProps[] }, slot) => {
      if (!acc[slot.weekday]) {
        acc[slot.weekday] = [];
      }
      acc[slot.weekday].push(slot);
      return acc;
    }, {});
  };

  const [groupedSlots, setGroupedSlots] = useState(
    groupSlotsByDay(sortedAvailabilitySlots),
  );

  const [originalSlots, setOriginalSlots] = useState(
    groupSlotsByDay(sortedAvailabilitySlots),
  );

  const resetChanges = () => {
    setGroupedSlots(originalSlots);
    enqueueTranslated('snackbar.calendarReverted', { variant: 'info' });
    setIsTimePickerChanged(false);
  };
  const handleAddSlot = (weekday: number) => {
    // Create a new empty slot
    const newSlot = { start: '', end: '', weekday };

    setGroupedSlots({
      ...groupedSlots,
      [weekday]: groupedSlots[weekday]
        ? [...groupedSlots[weekday], newSlot]
        : [newSlot],
    });
    setIsTimePickerChanged(true);
  };
  const handleDayClick = (weekday: number) => {
    if (!groupedSlots[weekday] || groupedSlots[weekday].length === 0) {
      handleAddSlot(weekday);
    }
  };
  const deleteSlot = (weekday: number, slotIndex: number) => {
    const filteredSlots = groupedSlots[weekday].filter(
      (_, index) => index !== slotIndex,
    );

    const newGroupedSlots = { ...groupedSlots };

    if (filteredSlots.length === 0) {
      // Remove the key entirely when no slots remain for this day
      delete newGroupedSlots[weekday];
    } else {
      newGroupedSlots[weekday] = filteredSlots;
    }

    setGroupedSlots(newGroupedSlots);
    setIsTimePickerChanged(true);
  };

  const handleEditSlot = (
    weekday: number,
    slotIndex: number,
    field: 'start' | 'end',
    newTime: string,
  ) => {
    const currentSlot = groupedSlots[weekday][slotIndex];

    const newTimeInMinutes = convertTimeStringToMinutes(newTime);
    const startTimeInMinutes = convertTimeStringToMinutes(currentSlot.start);
    const endTimeInMinutes = convertTimeStringToMinutes(currentSlot.end);

    // Clear any existing errors for this slot
    setSlotErrors((prev) => ({
      ...prev,
      [weekday]: {
        ...prev[weekday],
        [slotIndex]: { start: false, end: false },
      },
    }));

    if (field === 'end' && newTimeInMinutes < startTimeInMinutes) {
      enqueueTranslated('snackbar.endAfterStart', { variant: 'error' });
      return;
    }
    if (field === 'start' && newTimeInMinutes > endTimeInMinutes) {
      enqueueTranslated('snackbar.startAfterStart', { variant: 'error' });
      return;
    }

    // Create the updated slot to check for conflicts
    const updatedSlot = {
      ...currentSlot,
      [field]: newTime,
      weekday,
    };

    // Only check for conflicts if both start and end times are set
    if (
      updatedSlot.start &&
      updatedSlot.end &&
      updatedSlot.start !== '' &&
      updatedSlot.end !== ''
    ) {
      const conflicts = checkSlotConflicts(updatedSlot);

      if (conflicts.length > 0) {
        const organizationList = conflicts.join(', ');
        enqueueSnackbar(
          String(
            t('snackbar.calendarSlotConflictOrgs', { list: organizationList }),
          ),
          { variant: 'error' },
        );

        // Mark both start and end inputs as error
        setSlotErrors((prev) => ({
          ...prev,
          [weekday]: {
            ...prev[weekday],
            [slotIndex]: { start: true, end: true },
          },
        }));
        return;
      }
    }

    setGroupedSlots({
      ...groupedSlots,
      [weekday]: groupedSlots[weekday].map((slot, index) =>
        index === slotIndex ? { ...slot, [field]: newTime } : slot,
      ),
    });
    setIsTimePickerChanged(true);
  };

  const newSlots = Object.values(groupedSlots).flat();

  // Helper function to check if there are any slot errors
  const hasSlotErrors = (): boolean => {
    return Object.values(slotErrors).some((weekdayErrors) =>
      Object.values(weekdayErrors).some(
        (slotError) => slotError.start || slotError.end,
      ),
    );
  };
  function checkSlotCollisions(
    newSlots: { weekday: number; start: string; end: string }[],
  ): boolean {
    const slotsByWeekday: { [key: string]: { start: string; end: string }[] } =
      {};
    newSlots.forEach((slot) => {
      if (!slotsByWeekday[slot.weekday]) {
        slotsByWeekday[slot.weekday] = [];
      }
      slotsByWeekday[slot.weekday].push({ start: slot.start, end: slot.end });
    });

    for (let weekday in slotsByWeekday) {
      const slots = slotsByWeekday[weekday];

      slots.sort((a, b) => a.start.localeCompare(b.start));

      for (let i = 0; i < slots.length - 1; i++) {
        if (slots[i].end.localeCompare(slots[i + 1].start) > 0) {
          return true;
        }
      }
    }

    return false;
  }

  const onSubmit = async (availabilitySlots: AvailabilitySlotDto[]) => {
    try {
      const availabilitySlotsArray = Object.values(availabilitySlots);
      const saveResult = await onSave(availabilitySlotsArray);

      // User backed out at the schedule-conflict confirm modal — no change made.
      if ((saveResult as any)?.cancelled) {
        return;
      }

      enqueueTranslated('snackbar.calendarUpdated', { variant: 'success' });
      refetch();
    } catch (error: any) {
      if (error?.response?.data?.message) {
        const errorMessage = error.response.data.message;
        if (errorMessage.includes('outside organization working hours')) {
          enqueueTranslated('snackbar.outsideWorkingHours', {
            variant: 'error',
          });
        } else if (errorMessage.includes('does not work on weekday')) {
          enqueueTranslated('snackbar.orgClosedWeekday', { variant: 'error' });
        } else if (errorMessage.includes('Visit types not enabled')) {
          enqueueTranslated('snackbar.visitTypesUnavailable', {
            variant: 'error',
          });
        } else if (
          errorMessage.includes('availabilitySlots should not be empty')
        ) {
          enqueueTranslated('snackbar.availabilityListEmpty', {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
      } else {
        enqueueTranslated('snackbar.genericError', { variant: 'error' });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(
        { availabilitySlots },
        { abortEarly: false },
      );

      // Filter out slots from other organizations - only send slots for current organization
      const currentOrgSlots = newSlots.filter((slot) => !slot.organizationName);

      const convertedSlots = currentOrgSlots.map((slot) => ({
        ...slot,
        start: slot.start,
        end: slot.end,
        weekday: slot.weekday,
      }));

      if (checkSlotCollisions(convertedSlots)) {
        enqueueTranslated('snackbar.calendarSlotOverlap', { variant: 'error' });
      } else {
        onSubmit(convertedSlots);
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((validationError) => {
          enqueueSnackbar(validationError.message, { variant: 'error' });
        });
      } else {
        enqueueTranslated('snackbar.genericError', { variant: 'error' });
      }
    }
  };

  return (
    <Stack sx={{ padding: 3, paddingTop: 0, gap: 3, height: '100%' }}>
      {timeOff && timeOff.length > 0 && (
        <Stack>
          <Alert severity="warning">
            <AlertTitle>{t('calendar.schedule.unavailabilityTitle')}</AlertTitle>
            <Typography variant="body2">
              {t('calendar.schedule.unavailabilityBody')}
            </Typography>
            <Stack gap={0} mt={1}>
              {(timeOff as any[]).map((period, index) => {
                if (!period.startDate || !period.endDate) return null;
                const startDate = new Date(period.startDate);
                const endDate = new Date(period.endDate);
                const hasTime = period.startTime && period.endTime;
                return (
                  <Typography variant="subtitle2" fontWeight="bold" key={index}>
                    {startDate.toLocaleDateString(dateLocale)}
                    {hasTime && ` ${period.startTime}`} -{' '}
                    {endDate.toLocaleDateString(dateLocale)}
                    {hasTime && ` ${period.endTime}`}
                  </Typography>
                );
              })}
            </Stack>
          </Alert>
        </Stack>
      )}

      <Stack
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 3,
          flex: 1,
        }}
      >
        <Box
          sx={{
            width: 1,
            display: 'flex',
            gap: { xs: 1, sm: 1, md: 2, lg: 2 },
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {weekdaysShort.map((day, index) => {
            // Check if there are slots for current organization (slots without organizationName)
            const currentOrgSlotsForDay =
              groupedSlots[(index + 1) % 7]?.filter(
                (slot) => !slot.organizationName,
              ) || [];
            const hasCurrentOrgSlots = currentOrgSlotsForDay.length > 0;

            return (
              <Button
                disabled={isLoading || isReadonly}
                key={index}
                sx={{
                  flex: 1,
                  color: hasCurrentOrgSlots ? 'primary.dark' : 'common.black',
                  bgcolor: hasCurrentOrgSlots
                    ? alpha('#50CEFF', 0.16)
                    : alpha('#9AA5A9', 0.08),
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
                onClick={() => handleDayClick((index + 1) % 7)}
              >
                {day}
              </Button>
            );
          })}
        </Box>
        {weekdaysLong.map((day, index) => {
          if (groupedSlots[(index + 1) % 7]?.length > 0) {
            return (
              <Box key={index}>
                {groupedSlots[(index + 1) % 7]?.length > 0 && (
                  <Grid
                    sx={{
                      flexDirection: {
                        xs: 'column',
                        sm: 'column',
                        md: 'row',
                        lg: 'row',
                      },
                    }}
                    container
                    item
                    key={index}
                  >
                    <Grid
                      sx={{
                        mt: { xs: 0, sm: 0, md: 2, lg: 2, xl: 2 },
                        mb: { xs: 3, sm: 3, md: 3, lg: 0, xl: 0 },
                      }}
                      item
                      lg={2}
                    >
                      <Typography variant="body1" fontWeight={500}>
                        {day}
                      </Typography>
                    </Grid>
                    <Grid
                      sx={{ flexDirection: 'column', gap: 2 }}
                      xs={12}
                      sm={12}
                      md={12}
                      lg={10}
                      item
                      container
                    >
                      {organizationWorkingHours &&
                        (() => {
                          const weekdayIndex = (index + 1) % 7;
                          const daySlots = organizationWorkingHours.filter(
                            (slot) => slot.weekday === weekdayIndex,
                          );

                          if (daySlots.length > 0) {
                            return (
                              <Grid item container>
                                <Box
                                  sx={{
                                    width: '100%',
                                    p: 2,
                                    mb: 1,
                                    borderRadius: 1.5,
                                    bgcolor: alpha('#50CEFF', 0.08),
                                    border: '1px solid',
                                    borderColor: alpha('#50CEFF', 0.24),
                                  }}
                                >
                                  <Stack direction="row" spacing={1}>
                                    <Iconify
                                      icon="mdi:clock-outline"
                                      width={20}
                                      height={20}
                                      sx={{ color: 'primary.main' }}
                                    />
                                    <Stack>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          color: 'text.primary',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {t('calendar.schedule.facilityOpeningTitle')}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t('calendar.schedule.facilityOpeningCaption')}
                                      </Typography>
                                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        {daySlots.map((slot, slotIndex) => (
                                          <Typography
                                            key={slotIndex}
                                            variant="body2"
                                            sx={{
                                              color: 'text.secondary',
                                              fontFamily: 'monospace',
                                              fontSize: '0.875rem',
                                            }}
                                          >
                                            {slot.start} -{' '}
                                            {slot.end}
                                          </Typography>
                                        ))}
                                      </Stack>
                                    </Stack>
                                  </Stack>
                                </Box>
                              </Grid>
                            );
                          }
                          return null;
                        })()}
                      {groupedSlots[(index + 1) % 7]?.map((slot, slotIndex) => (
                        <Grid
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                          }}
                          item
                          container
                          key={slotIndex}
                        >
                          <TimePicker
                            disabled={
                              isLoading || isReadonly || !!slot.organizationName
                            }
                            orientation="portrait"
                            label={String(t('calendar.schedule.timeFrom'))}
                            minutesStep={1}
                            value={convertTimeStringToDate(slot.start)}
                            minTime={convertTimeStringToDate('00:01')}
                            onChange={(newTime) =>
                              handleEditSlot(
                                slot.weekday,
                                slotIndex,
                                'start',
                                newTime ? convertDateToTimeString(newTime) : '',
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                sx={{ flex: 1 }}
                                {...params}
                                error={
                                  slotErrors[slot.weekday]?.[slotIndex]
                                    ?.start || false
                                }
                              />
                            )}
                          />
                          <Box sx={{ marginX: 3 }}>-</Box>

                          <TimePicker
                            disabled={
                              isLoading || isReadonly || !!slot.organizationName
                            }
                            orientation="portrait"
                            label={String(t('calendar.schedule.timeTo'))}
                            minutesStep={1}
                            maxTime={convertTimeStringToDate('23:59')}
                            minTime={addMinutes(
                              convertTimeStringToDate(slot.start),
                              1,
                            )}
                            value={convertTimeStringToDate(slot.end)}
                            onChange={(newTime) => {
                              return handleEditSlot(
                                slot.weekday,
                                slotIndex,
                                'end',
                                newTime ? convertDateToTimeString(newTime) : '',
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                sx={{ flex: 1 }}
                                {...params}
                                error={
                                  slotErrors[slot.weekday]?.[slotIndex]?.end ||
                                  false
                                }
                              />
                            )}
                          />
                          {!isReadonly && !slot.organizationName && (
                            <Button
                              disabled={isLoading}
                              sx={{
                                color: 'common.black',
                                '&:hover': {
                                  backgroundColor: alpha('#9AA5A9', 0.08),
                                },
                                marginLeft: 3,
                              }}
                              onClick={() =>
                                deleteSlot((index + 1) % 7, slotIndex)
                              }
                            >
                              <Iconify
                                width="20px"
                                height="20px"
                                icon="ic:round-close"
                              />
                            </Button>
                          )}
                          {slot.organizationName && (
                            <Button
                              disabled={isLoading}
                              sx={{
                                color: 'common.black',
                                '&:hover': {
                                  backgroundColor: alpha('#9AA5A9', 0.08),
                                },
                                marginLeft: 3,
                              }}
                              onClick={(event) => {
                                setPopoverAnchorEl(event.currentTarget);
                                setPopoverContent(
                                  String(
                                    t('calendar.schedule.slotAssignedToOrg', {
                                      name: slot.organizationName,
                                    }),
                                  ),
                                );
                              }}
                            >
                              <Iconify
                                width="20px"
                                height="20px"
                                icon="ic:outline-info"
                                sx={{
                                  color: 'info.main',
                                }}
                              />
                            </Button>
                          )}
                        </Grid>
                      ))}
                      {!isReadonly && (
                        <Button
                          disabled={isLoading}
                          sx={{
                            width: 195,
                            color: 'common.black',
                            '&:hover': {
                              backgroundColor: alpha('#9AA5A9', 0.08),
                            },
                            mt: 1,
                            mb: 3,
                          }}
                          size="small"
                          onClick={() => handleAddSlot((index + 1) % 7)}
                        >
                          <Iconify
                            width="20px"
                            height="20px"
                            icon="ic:round-plus"
                          />
                          <Typography variant="subtitle2">
                            {t('calendar.schedule.addInterval')}
                          </Typography>
                        </Button>
                      )}
                      {isReadonly && <Box sx={{ marginY: 0.5 }}></Box>}
                    </Grid>
                    {(() => {
                      // Find all day indices that have slots (length > 0)
                      const daysWithSlots = weekdaysLong
                        .map((_, i) => i)
                        .filter((i) => groupedSlots[(i + 1) % 7]?.length > 0);

                      // Check if current day is the last one with slots
                      const isLastDayWithSlots =
                        index === daysWithSlots[daysWithSlots.length - 1];

                      return (
                        !isLastDayWithSlots && <Divider sx={{ width: 1 }} />
                      );
                    })()}
                  </Grid>
                )}
              </Box>
            );
          }
          return null;
        })}
        {Object.values(groupedSlots).length === 0 && (
          <Stack
            sx={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Image
              sx={{ width: '200px' }}
              alt="empty waiting room"
              src="/assets/images/empty_table.svg"
            />
            <Typography>
              {t('calendar.schedule.pickWeekdayForAvailability')}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Stack flexDirection="column" marginTop={2} width={1}>
        {weekdaysShort.map((day, index) => {
          if (groupedSlots[(index + 1) % 7]?.length > 0) {
            return (
              <Divider
                sx={{
                  width: 1,
                  zIndex: 1,
                  borderColor: LIGHT_BACKGROUND.default,
                  position: 'absolute',
                  bottom: 92,
                  right: 0,
                }}
                key={index}
              />
            );
          }
          return null;
        })}

        {!isReadonly && (
          <Box
            sx={{ width: 1, display: 'flex', gap: 2, justifyContent: 'end' }}
          >
            <Button
              sx={{ float: 'right', width: '124px' }}
              type="submit"
              variant="outlined"
              color="inherit"
              onClick={resetChanges}
              disabled={!isTimePickerChanged || isLoading}
            >
              {t('calendar.schedule.discardChanges')}
            </Button>
            <Button
              sx={{ float: 'right', width: '75px' }}
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!isTimePickerChanged || isLoading || hasSlotErrors()}
            >
              {t('common.save')}
            </Button>
          </Box>
        )}

        <MenuPopover
          open={popoverAnchorEl}
          onClose={() => setPopoverAnchorEl(null)}
          arrow="bottom-center"
          sx={{
            width: 'auto',
            maxWidth: 300,
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ p: 1 }}>
            <Typography variant="body2">{popoverContent}</Typography>
          </Box>
        </MenuPopover>
      </Stack>
    </Stack>
  );
};

export default CalendarSchedule;
